import { WINDOW } from '@modules/window-token/window-token.module';
import { SwiperConfigInterface } from '@app/shared/swiper/swiper.interfaces';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface ShowingConfig {
  desktop?: boolean;
  mobile?: boolean;
}

@Component({
  selector: 'app-swiper',
  templateUrl: './swiper.component.html',
  styleUrls: ['./swiper.component.scss']
})
export class SwiperComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() swiperConfig: SwiperConfigInterface = {};
  @Input() withNavigation = false;
  @Input() isLoading: boolean;
  @Input() isRoundNavigation = false;
  @Input() withPagination = false;
  @Input() isSameSlidesHeight = false;
  @Input() paginationShowingConfig: ShowingConfig = {
    desktop: true,
    mobile: true,
  };
  @Input() navigationShowingConfig: ShowingConfig = {
    desktop: true,
    mobile: true,
  };

  @Output() currentSlideIndexChange = new EventEmitter<number>();

  @ViewChild('swiperContainer') swiperContainer: ElementRef;
  @ViewChild('swiperSlides') swiperSlides: ElementRef;
  @ViewChild('next') next: ElementRef;
  @ViewChild('prev') prev: ElementRef;

  slidesCount = 0;
  private currentSlide = 0;
  private currentSlideElement: HTMLElement = null;

  // When calling this.slideTo(slideNum) in the same eventloop frame which populates swiper slides,
  // swiper actually hadn't rendered them yet. This variable holds the sildeNum to which
  // we should slideTo() when child slides get rendered.
  private delayedSlideTo: { slideIndex: number, transitionDelayMs: number } = null;

  private swiper: any = null;

  constructor(
    @Inject(WINDOW) private window: any,
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
  ) {  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.isSameSlidesHeight && changes.isSameSlidesHeight.currentValue) {
      if (isPlatformBrowser(this.platformId)) this.swiperContainer.nativeElement.onresize = () => this.onWindowResizeForSameSlidesHeight();
    }
  }

  ngOnInit() {
    if (this.window.Swiper) {
      this.swiper = new this.window.Swiper(this.swiperContainer.nativeElement, this.config);
      this.swiper.on('slideChange', () => this.zone.run(() => this.updateCurrentSlide()));
      this.updateSwiper();
    }
    if (isPlatformBrowser(this.platformId) && this.window) {
      this.onWindowResizeForSameSlidesHeight();
      if (this.config && this.config.autoplayDisableOnInteraction) {
        this.stopAutoPlayOnMouseover();
        this.stopAutoplayOnMobile();
      }
    }
  }

  ngOnDestroy() {
    if (this.swiper) {
      this.swiper.detachEvents();
    }
  }

  ngAfterViewChecked() {
    if (isPlatformBrowser(this.platformId)) setTimeout(() => this.updateSwiper(), 10);
    else this.updateSwiper();
  }

  private updateCurrentSlide() {
    if (this.swiper.realIndex === this.currentSlide) return;
    this.currentSlide = this.swiper.realIndex;
    this.currentSlideElement = this.swiper.slides[this.swiper.realIndex];
    this.currentSlideIndexChange.emit(this.currentSlide);
  }

  private indexOfSlideElement(slideElement: HTMLElement) {
    // I guess we should probably introduce a unique `key` attribute on the slides
    // instead (akin to React). Or stop working with indexes completely by switching
    // to HTMLElements or Angular ElementRefs.
    //
    // This (comparing by equality of slides' HTMLElement) *should* be fine, unless
    // slides get removed or HTMLElements of the same slides end up re-created,
    // thus loosing the reference to the current slide.
    const slidesCount = this.swiper.slides.length;
    for (let i = 0; i < slidesCount; i++) {
      if (this.swiper.slides['' + i] === slideElement) {
        return i;
      }
    }
    return this.currentSlide;
  }

  get config(): SwiperConfigInterface {
    return {
      ...this.swiperConfig,
      ...(this.withNavigation ? {
        navigation: {
          nextEl: this.next.nativeElement,
          prevEl: this.prev.nativeElement,
        },
      } : {}),
      ...(this.withPagination ? {
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          dynamicBullets: true,
          dynamicMainBullets: 1,
          ...(this.swiperConfig.pagination ? this.swiperConfig.pagination : {}),
        },
      } : {}),
    };
  }

  private updateSwiper() {
    if (!this.swiper) return;
    const currentSlidesCount = this.swiperSlides.nativeElement.childElementCount;

    if (currentSlidesCount !== this.slidesCount) {
      this.slidesCount = currentSlidesCount;
      this.swiper.update();
      this.swiper.slideTo(this.indexOfSlideElement(this.currentSlideElement), 0);
    }

    this.updateCurrentSlide();

    if (this.delayedSlideTo != null && this.delayedSlideTo.slideIndex < currentSlidesCount) {
      this.swiper.slideTo(this.delayedSlideTo.slideIndex, this.delayedSlideTo.transitionDelayMs);
      this.delayedSlideTo = null;
    }
  }

  slideTo(slideIndex: number, transitionDelayMs?: number) {
    if (slideIndex >= this.slidesCount) {
      this.delayedSlideTo = { slideIndex, transitionDelayMs };
    } else {
      if (this.swiper) this.swiper.slideTo(slideIndex, transitionDelayMs);
    }
  }

  get hasSwiper(): boolean {
    return this.swiper;
  }

  private onWindowResizeForSameSlidesHeight() {
    if (this.window.innerWidth < 768) return;

    const slides = this.swiperContainer.nativeElement.getElementsByClassName('swiper-slide');
    let maxHeight = 0;
    for (let index = 0; index < slides.length; index++) {
      maxHeight = maxHeight <= slides[index].offsetHeight ? slides[index].offsetHeight : maxHeight;
      if (slides[index].offsetHeight < maxHeight) slides[index].style.height = maxHeight + 'px';
    }
  }

  private stopAutoPlayOnMouseover() {
    if (!this.swiper || !this.swiper.autoplay) return;
    this.swiperContainer.nativeElement.onclick = () => this.swiper.autoplay.stop();
  }

  private stopAutoplayOnMobile() {
    if (!this.swiper.autoplay) return;

    this.window.onresize = () => {
      if (this.window.innerWidth < 768 && this.swiper.autoplay.running) this.swiper.autoplay.stop();
      if (this.window.innerWidth > 768 && !this.swiper.autoplay.running) this.swiper.autoplay.start();
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/swiper/swiper.component.ts